/**
 * Builds the WagePro migration template the user fills in and re-uploads.
 *
 * ONE source of truth drives both formats: `MIGRATION_SHEETS` +
 * `columnsForJurisdiction` from `@jumpstone/core`. The `.xlsx` is a single
 * workbook (Instructions + the four data sheets + a hidden Lists sheet backing
 * the dropdowns), with every id/account column stored as Text so leading zeros
 * survive and no NIC becomes scientific notation. The CSV form is four files
 * with the identical machine-name headers, so the server's header matching and
 * validation are format-agnostic (ADR-093 port/adapter).
 *
 * Pure data assembly — no download side effect, no money maths (the template
 * is empty; money parsing happens server-side). The screen triggers the
 * actual download via `lib/download`.
 */
import {
  MIGRATION_DATA_SHEETS,
  MIGRATION_SHEETS,
  columnsForJurisdiction,
  idTextColumns,
  type MigrationColumn,
  type MigrationJurisdiction,
  type MigrationSheetSpec,
} from '@jumpstone/core';
import {
  buildCsv,
  buildWorkbookXlsx,
  type ColumnValidation,
  type WorkbookSheetSpec,
} from '@jumpstone/spreadsheet';

export interface MigrationTemplateContext {
  jurisdiction: MigrationJurisdiction;
  base_currency: string;
  enabled_currencies: string[];
  leave_type_codes: string[];
  cutover_date: string;
}

/** Machine-name headers for one sheet, scoped to the jurisdiction. */
function headersFor(
  sheet: MigrationSheetSpec,
  jurisdiction: MigrationJurisdiction,
): string[] {
  return columnsForJurisdiction(sheet, jurisdiction).map((c) => c.name);
}

/** The dropdown options a given column offers, or null for a free column. */
function optionsForColumn(
  column: MigrationColumn,
  ctx: MigrationTemplateContext,
): string[] | null {
  if (column.name === 'pay_currency') {
    return ctx.enabled_currencies.length > 0 ? ctx.enabled_currencies : null;
  }
  if (column.name === 'leave_type_code') {
    return ctx.leave_type_codes.length > 0 ? ctx.leave_type_codes : null;
  }
  if (column.kind === 'enum' && column.enumValues) {
    return column.enumValues.map((v) => String(v));
  }
  if (column.kind === 'boolean') {
    return ['true', 'false'];
  }
  return null;
}

/** Build the data-validation list for a sheet's columns (dropdowns + dates). */
function validationsFor(
  columns: MigrationColumn[],
  ctx: MigrationTemplateContext,
): ColumnValidation[] {
  const validations: ColumnValidation[] = [];
  columns.forEach((column, col_index) => {
    const options = optionsForColumn(column, ctx);
    if (options) {
      validations.push({ col_index, type: 'list', options });
      return;
    }
    if (column.kind === 'date') {
      validations.push({ col_index, type: 'date' });
    }
  });
  return validations;
}

/** 0-based indexes of the id/account (Text-stored) columns for a sheet. */
function textColumnIndexes(
  columns: MigrationColumn[],
  idNames: Set<string>,
): number[] {
  const indexes: number[] = [];
  columns.forEach((column, i) => {
    if (idNames.has(column.name)) indexes.push(i);
  });
  return indexes;
}

/** The read-only Instructions sheet rows (one cell per line). */
function instructionRows(ctx: MigrationTemplateContext): string[][] {
  return [
    ['WagePro import template'],
    [
      `Cut-over date: ${ctx.cutover_date || '(set in the wizard)'} — import pay history up to this date.`,
    ],
    [`Base currency: ${ctx.base_currency}. Enter amounts like 125000.00.`],
    ['One row per employee on the Employees sheet (required).'],
    [
      'Salary_History, Payroll_History and Leave_Balances are optional but recommended.',
    ],
    ['Dates use YYYY-MM-DD. ID and account columns are stored as text.'],
    ['Do not rename the header row. Leave columns you do not use blank.'],
  ];
}

export interface MigrationTemplateFiles {
  xlsx: Uint8Array;
  csvs: { filename: string; content: string }[];
}

/** Lowercase `<sheet>.csv` filename for a data sheet. */
export function csvFilenameFor(sheetName: string): string {
  return `${sheetName.toLowerCase()}.csv`;
}

/** Build both the .xlsx workbook and the four per-sheet CSV files. */
export function buildMigrationTemplate(
  ctx: MigrationTemplateContext,
): MigrationTemplateFiles {
  const dataSpecs = MIGRATION_DATA_SHEETS.map((name) => MIGRATION_SHEETS[name]);

  // CSVs: identical machine-name headers, no data rows.
  const csvs = dataSpecs.map((sheet) => ({
    filename: csvFilenameFor(sheet.name),
    content: buildCsv(headersFor(sheet, ctx.jurisdiction), []),
  }));

  // Collect every dropdown option set for the hidden Lists sheet.
  const listColumns: { label: string; options: string[] }[] = [];
  const sheetSpecs: WorkbookSheetSpec[] = [];

  // Instructions first.
  sheetSpecs.push({
    name: 'Instructions',
    headers: [],
    rows: instructionRows(ctx),
  });

  for (const sheet of dataSpecs) {
    const columns = columnsForJurisdiction(sheet, ctx.jurisdiction);
    const idNames = new Set(idTextColumns(sheet));
    for (const column of columns) {
      const options = optionsForColumn(column, ctx);
      if (options && !listColumns.some((l) => l.label === column.name)) {
        listColumns.push({ label: column.name, options });
      }
    }
    sheetSpecs.push({
      name: sheet.name,
      headers: columns.map((c) => c.name),
      rows: [],
      validations: validationsFor(columns, ctx),
      textColumns: textColumnIndexes(columns, idNames),
    });
  }

  // Hidden Lists sheet: one labelled column per dropdown, values stacked down.
  if (listColumns.length > 0) {
    const maxLen = Math.max(...listColumns.map((l) => l.options.length));
    const listRows: string[][] = [];
    for (let r = 0; r < maxLen; r++) {
      listRows.push(listColumns.map((l) => l.options[r] ?? ''));
    }
    sheetSpecs.push({
      name: 'Lists',
      headers: listColumns.map((l) => l.label),
      rows: listRows,
      hidden: true,
    });
  }

  return { xlsx: buildWorkbookXlsx(sheetSpecs), csvs };
}
